import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { signUpWithEmail } from '@/lib/platform/auth';
import { enterAfterAuth } from '@/lib/platform/routing';
import { entry } from '@/routes';

export function SignUpPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const handleSignUp = async () => {
    setMessage(null);
    setIsPending(true);

    const result = await signUpWithEmail(name, email, password);
    if (result.ok) {
      await enterAfterAuth(entry);
      return;
    }

    setMessage(result.message);
    setIsPending(false);
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-12 font-sans text-foreground">
      <div className="w-full max-w-md">
        <header className="mb-8 space-y-3">
          <p className="text-sm font-semibold tracking-[0.18em] text-accent-foreground uppercase">TrustFile</p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Create your account</h1>
          <p className="text-sm leading-6 text-muted-foreground">
            Set up secure access to your estate and trust filing workspace.
          </p>
        </header>

        <div className="space-y-8">
          <section aria-labelledby="sign-up-form-heading">
            <h2 id="sign-up-form-heading" className="sr-only">
              Sign-up form
            </h2>
            <div className="rounded-[0.375rem] border border-border bg-card p-6 shadow-sm">
              <div className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="sign-up-name">Name</Label>
                  <Input
                    id="sign-up-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="h-11 rounded-[0.375rem]"
                    disabled={isPending}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sign-up-email">Email</Label>
                  <Input
                    id="sign-up-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="h-11 rounded-[0.375rem]"
                    disabled={isPending}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sign-up-password">Password</Label>
                  <Input
                    id="sign-up-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="h-11 rounded-[0.375rem]"
                    disabled={isPending}
                  />
                </div>
                {message ? (
                  <p role="alert" className="text-sm leading-5 text-destructive">
                    {message}
                  </p>
                ) : null}
                <Button
                  type="button"
                  data-jr-key="sign-up-submit"
                  disabled={isPending}
                  onClick={() => void handleSignUp()}
                  className="h-11 w-full rounded-[0.375rem] bg-primary text-primary-foreground hover:bg-primary/85 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isPending ? 'Creating account…' : 'Create account'}
                </Button>
              </div>
            </div>
          </section>

          <section aria-labelledby="sign-up-help-heading" className="space-y-4">
            <h2 id="sign-up-help-heading" className="text-sm font-semibold text-foreground">
              About your account
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              Your account starts without an application role. Access to TrustFile filing screens is granted by your
              firm when appropriate.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-sm">
              <Link
                to="/sign-in"
                className="font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-[0.375rem] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                Sign in instead
              </Link>
              <Link
                to="/"
                className="font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-[0.375rem] focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                Return to TrustFile
              </Link>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
